import React, { useEffect, useState } from 'react'
import { getConfig } from '../config/gameConfig';
import useSound from "use-sound";
import play from "../assets/sounds_play.mp3";
import correct from "../assets/sounds_correct.mp3";
import wrong from "../assets/sounds_wrong.mp3";
import { trackProgress } from '../utils/progressApi';

export default function Trivia({ userName, setStop, setQuestionNumber, questionNumber, questions, setTimerActive, fiftyUsed, changeUsed, setFiftyUsed, onFifty, onChange, questionSet, onChangeUsed, extraQuestion }) {
    // state for a single question 
    const [question, setQuestion] = useState(null);
    // state for the shuffled answers
    const [shuffledAnswers, setShuffledAnswers] = useState([]);
    //state for the selected answer
    const [selectedAnswer, setSelectedAnswer] = useState(null)
    //state for the class names that cause animation on the selected option
    const [className, setClassName] = useState("answer")
    // Add state for 50-50 filtered answers
    const [fiftyAnswers, setFiftyAnswers] = useState(null);
    // Add state for changed question
    const [changedQuestion, setChangedQuestion] = useState(false);
    // Track which question number 50-50 was used on
    const [fiftyQuestionNumber, setFiftyQuestionNumber] = useState(null);

    //sound for correct answer
    const [correctAnswer] = useSound(correct);
    //sound for wrong answer
    const [wrongAnswer] = useSound(wrong);
    //initial sound for the start of the quiz
    const [letsPlay] = useSound(play);

    useEffect(() => {
        // play the sound on componentDidMount 
        letsPlay()
    }, [letsPlay]);


    useEffect(() => {
        // setting the question from the list of questions
        let q = questions[questionNumber - 1];
        // If Change lifeline used, swap question
        if (changeUsed && extraQuestion && !changedQuestion) {
            if (getConfig('debugMode')) {
                console.log('🔄 Change lifeline activated!', { 
                    originalQuestion: q?.question, 
                    newQuestion: extraQuestion?.question,
                    extraQuestion: extraQuestion,
                    changedQuestion: changedQuestion
                });
            }
            q = extraQuestion;
            setChangedQuestion(true);
            // Reset 50-50 state when changing question
            setFiftyUsed && setFiftyUsed(false);
            setFiftyAnswers(null);
            setFiftyQuestionNumber(null);
        }
        setQuestion(q);
        if (q && getConfig('shuffleAnswers')) {
            // Shuffle answers
            const shuffled = [...q.answers].sort(() => Math.random() - 0.5);
            setShuffledAnswers(shuffled);
        } else if (q) {
            setShuffledAnswers(q.answers);
        } else {
            setShuffledAnswers([]);
        }
        setFiftyAnswers(null); // reset 50-50 on question change
        setClassName("answer"); // reset className to clear previous styling
        setSelectedAnswer(null); // reset selected answer
    }, [questionNumber, questions, changeUsed, extraQuestion, setFiftyUsed]);

    useEffect(() => {
        // If 50-50 is used, filter answers only for the question where it was used
        if (fiftyUsed && question && fiftyQuestionNumber === questionNumber && !fiftyAnswers) {
            const correct = question.answers.find(a => a.correct);
            const incorrects = question.answers.filter(a => !a.correct);
            const randomIncorrect = incorrects[Math.floor(Math.random() * incorrects.length)];
            setFiftyAnswers([correct, randomIncorrect].sort(() => Math.random() - 0.5));
        }
    }, [fiftyUsed, question, fiftyAnswers, fiftyQuestionNumber, questionNumber]);

    // When 50-50 is clicked, record the question number
    useEffect(() => {
        if (fiftyUsed && fiftyQuestionNumber === null) {
            setFiftyQuestionNumber(questionNumber);
        }
        // Reset for new game
        if (!fiftyUsed) {
            setFiftyQuestionNumber(null);
        }
    }, [fiftyUsed, questionNumber, fiftyQuestionNumber]);

    useEffect(() => {
        setChangedQuestion(false);
    }, [questionNumber]);
    
    // Handle changeUsed state changes
    useEffect(() => {
        if (changeUsed && extraQuestion && !changedQuestion) {
            if (getConfig('debugMode')) {
                console.log('🔄 Change lifeline triggered by changeUsed change!', { 
                    changeUsed, 
                    extraQuestion: extraQuestion?.question,
                    changedQuestion
                });
            }
            setQuestion(extraQuestion);
            setChangedQuestion(true);
            
            if (getConfig('debugMode')) {
                console.log('✅ Question state updated to:', extraQuestion?.question);
            }
            // Reset 50-50 state when changing question
            setFiftyUsed && setFiftyUsed(false);
            setFiftyAnswers(null);
            setFiftyQuestionNumber(null);
            
            // Update shuffled answers for the new question
            if (extraQuestion && getConfig('shuffleAnswers')) {
                const shuffled = [...extraQuestion.answers].sort(() => Math.random() - 0.5);
                setShuffledAnswers(shuffled);
            } else if (extraQuestion) {
                setShuffledAnswers(extraQuestion.answers);
            }
        }
        
        // Reset changedQuestion when changeUsed becomes false (after user answers the changed question)
        if (!changeUsed) {
            setChangedQuestion(false);
        }
    }, [changeUsed, extraQuestion, changedQuestion, setFiftyUsed]);

    useEffect(() => {
        // Reset 50-50 answers when question changes
        setFiftyAnswers(null);
    }, [questionNumber]);

    //Custom function for timeout with duration and a callback function as parameters
    const delay = (duration, callback) => {
        setTimeout(() => {
            callback()
        }, duration)
    }

    const handleClick = (a) => {
        if (getConfig('debugMode')) {
            console.log('handleClick called', { a, questionNumber, questionsLength: questions.length, isLastQuestion: questionNumber === questions.length });
        }
        setSelectedAnswer(a)
        setClassName("answer active")
        setTimerActive && setTimerActive(false);
        delay(getConfig('answerRevealDelay'), () => setClassName(a.correct ? "answer correct" : "answer wrong"))
        delay(getConfig('nextQuestionDelay'), () => {
            if (a.correct) {
                correctAnswer()
                delay(getConfig('transitionDelay'), () => {
                    if (questions.length !== questionNumber) {
                        setQuestionNumber(prev => prev + 1);
                        setSelectedAnswer(null);
                    } else {
                        setQuestionNumber(prev => prev + 1);
                        setStop(true);
                        setSelectedAnswer(null);
                    }
                    if (changedQuestion && typeof onChangeUsed === 'function') onChangeUsed();
                })
            } else {
                wrongAnswer();
                delay(getConfig('transitionDelay'), async () => {
                    // Track error with correct answer in notes
                    const correctAnswerObj = question.answers.find(ans => ans.correct);
                    await trackProgress({
                        username: userName,
                        questionset: questionSet,
                        action: 'error',
                        question: `Q${questionNumber}: ${question.question}`,
                        answer: a.text,
                        notes: correctAnswerObj ? `Correct answer: ${correctAnswerObj.text}` : 'Correct answer not found'
                    });
                    
                    setStop(true);
                    if (changedQuestion && typeof onChangeUsed === 'function') onChangeUsed();
                })
            }
        })
    }
    const prefixes = ["Α.", "Β.", "Γ.", "Δ."];
    
    // Debug: Log what question is being rendered
    useEffect(() => {
        if (question && getConfig('debugMode')) {
            console.log('📝 Rendering question:', question.question);
        }
    }, [question]);

    return (
        <div className='trivia'>
            <div className="user-info">
                {userName}
            </div>
            <div className="question">{question?.question}</div>
            <div className="answers">
                {
                    (fiftyUsed && fiftyAnswers && fiftyQuestionNumber === questionNumber ? fiftyAnswers : shuffledAnswers).map((a) => {
                        // Only disable answers for the question where 50-50 was used
                        const isFiftyActive = fiftyUsed && fiftyAnswers && fiftyQuestionNumber === questionNumber;
                        const originalIndex = shuffledAnswers.findIndex(sa => sa.text === a.text);
                        return (
                            <div
                                key={`q${questionNumber}-${originalIndex}-${a.text.substring(0, 10)}`}
                                className={selectedAnswer === a ? className : "answer"}
                                onClick={() => !selectedAnswer && (!isFiftyActive || fiftyAnswers.includes(a)) ? handleClick(a) : undefined}
                                style={{
                                    pointerEvents: selectedAnswer ? 'none' : (isFiftyActive ? (fiftyAnswers.includes(a) ? 'auto' : 'none') : 'auto'),
                                    opacity: selectedAnswer ? 1 : (isFiftyActive ? (fiftyAnswers.includes(a) ? 1 : 0.5) : 1)
                                }}
                            >
                                {`${prefixes[originalIndex]} ${a.text}`}
                            </div>
                        );
                    })
                }
            </div>
        </div>
    )
}
